import { TranslateSvc } from '../../services/TranslateSvc';
import { HttpSvcFactory } from '../../services/HttpSvc';
import { Utils } from '../../underscore-extensions';
import {
    AuthenticationSvc,
    USER_LOGOUT
} from '../../services/AuthenticationSvc';
import { AnalyticSvc } from '../../services/AnalyticSvc';
import { AffiliateSvc } from '../../services/AffiliateSvc';

export const UPDATE_SUBSCRIPTION_CARD = 'website/subscription/updateCard';
export const APPLY_COUPON = 'website/subscription/applyCoupon';
export const CouponCompaniesNames = {
    prod: {
        AppSumo117: {
            title: 'AppSumo'
        },
        MightyDeals417: {
            title: 'MightyDeals'
        },
        StackSocial0718: {
            title: 'StackSocial'
        },
        'StackSocial0219-1A': {
            title: 'StackSocial'
        },
        defaultCompany: {
            title: 'Company'
        }
    },

    dev: {
        MightyDeals417: {
            title: 'AppSumo'
        },
        MightyDeals417Test: {
            title: 'MightyDeals'
        },
        StackSocial0718: {
            title: 'StackSocial'
        },
        'StackSocial0219-1A': {
            title: 'StackSocial'
        },
        defaultCompany: {
            title: 'Company'
        }
    }
}[options.isProduction ? 'prod' : 'dev'];

export class SubscriptionSvc {
    cardDetailsCashed: anm.CardDetails;
    cardDetailsErrorMessage: string;
    upgradesDiscount: number;
    isExpired: boolean;
    totalPriceLastValue: number;

    private isStripeError: boolean;
    private specialSubscriptionNames = ['WaveBigAgency', 'WaveSmallAgency'];

    constructor(
        private $q: ng.IQService,
        private $filter: ng.IFilterService,
        private $http: ng.IHttpService,
        private $rootScope: ng.IRootScopeService,
        private $state: ng.ui.IStateService,
        private TranslateSvc: TranslateSvc,
        private analyticSvc: AnalyticSvc,
        private AuthenticationSvc: AuthenticationSvc,
        private affiliateSvc: AffiliateSvc,
        private HttpSvc: HttpSvcFactory
    ) {
        this.$rootScope.$on(USER_LOGOUT, () => {
            this.upgradesDiscount = undefined;
            this.cardDetailsCashed = undefined;
            this.cardDetailsErrorMessage = undefined;
        });
    }

    static isStripeError(error: any): error is StripeError {
        return error && error.message && error.type;
    }

    get product(): AnimatronProduct {
        return Utils.getProduct();
    }

    get productParams() {
        return { params: { product: this.product } };
    }

    getError(error?: string | StripeError): string {
        if (!error) return null;

        if (_.isString(error)) {
            return (
                this.getStripeCouponError(error) ||
                this.getRequiredSubscriptionError(error) ||
                this.getStripeCardError(error) ||
                (this.TranslateSvc.has(error.toLowerCase()) &&
                    this.TranslateSvc.get(error.toLowerCase())) ||
                error
            );
        } else if (SubscriptionSvc.isStripeError(error)) {
            return error.message;
        } else {
            return error as string;
        }
    }

    findSubscriptionByName(
        displayName: string
    ): ng.IPromise<anm.SubscriptionDetails> {
        // search in upgrades
        const upgrade = this.getUpgradesByName(displayName);
        if (upgrade) return this.$q.resolve(upgrade);

        // search in current subscription
        return this.getDowngradesByName(null, displayName).then(downgrade => {
            if (downgrade) return downgrade;

            const currentSubscription = this.getDetails();
            if (currentSubscription.displayName === displayName)
                return this.$q.resolve(currentSubscription);

            return this.$q.reject(`Incorrect displayName = ${displayName}`);
        });
    }

    getPredictPrice({
        displayName,
        coupon,
        ...params
    }: SubscriptionPredict.Params): ng.IPromise<SubscriptionPredict.Details> {
        return this.findSubscriptionByName(displayName)
            .then(({ name }) =>
                this.HttpSvc('subscription/calculate').secureGet({
                    params: {
                        ...params,
                        name,
                        coupon: coupon || undefined
                    }
                })
            )
            .then(r => r.data);
    }

    isStripe() {
        return !this.isStripeError && options.user.isStripe();
    }

    getAnnualSave(upgrade = this.getDetails()) {
        return (upgrade.monthlyPrice * 12 - upgrade.annualPrice) / 100;
    }

    getAnnualPrice(
        details: anm.SubscriptionDetails = this.getDetails(),
        discountPercentOff = details.discount && details.discount.percentOff
    ) {
        return this.round(
            (details.annualPrice * this.calcDiscount(discountPercentOff)) /
                100 /
                12
        );
    }

    getMonthlyPrice(
        details: anm.SubscriptionDetails = this.getDetails(),
        discountPercentOff = details.discount && details.discount.percentOff
    ) {
        return this.round(
            (details.monthlyPrice * this.calcDiscount(discountPercentOff)) / 100
        );
    }

    isFree(currentSubscription = this.getDetails()) {
        const freeSubscriptions = ['WaveFree', 'Free'];
        return freeSubscriptions.includes(currentSubscription.name);
    }

    isHigh(currentSubscription = this.getDetails()) {
        const highSubscriptions = ['WaveBusiness', 'Business'];
        return highSubscriptions.includes(currentSubscription.name);
    }

    isSpecial(currentSubscription = this.getDetails()) {
        return this.specialSubscriptionNames.includes(currentSubscription.name);
    }

    isAppSumo({ billingPeriod, discount } = this.getDetails()) {
        return (
            discount &&
            (discount.provider === 'AppSumo117' ||
                (!options.isProduction &&
                    discount.provider === 'MightyDeals417'))
        );
    }

    isCurrentOldPlan() {
        const oldPlans = [
            'WavePro',
            'WaveAgency',
            'WaveAgency2',
            'WaveUltimate2'
        ];
        const { name } = this.getDetails();

        return oldPlans.includes(name);
    }

    isSameOldPlan(period: 'annual' | 'monthly', planName: string) {
        const { name: currentPlanName } = this.getDetails();

        const monthlyPlanMap = {
            WavePro: 'Agency',
            WaveAgency2: 'Corporate'
        };

        const annualPlanMap = {
            WavePro: 'Pro',
            WaveAgency2: 'Agency',
            WaveUltimate2: 'Corporate'
        };

        const currentUserPlan =
            period === 'annual'
                ? annualPlanMap[currentPlanName]
                : monthlyPlanMap[currentPlanName];

        const isSamePlan = currentUserPlan === planName;

        return isSamePlan && this.isCurrentOldPlan();
    }

    getDetails(): anm.SubscriptionDetails {
        const product = this.product.toLowerCase();
        const subscription = options.user.subscriptionDetails[product];
        return this.prepareSubscription(subscription);
    }

    setUpgradesDiscount() {
        this.affiliateSvc
            .getAffiliateUser()
            .then((affiliateInfo: anm.AffiliateInfo) => {
                this.upgradesDiscount = affiliateInfo.discount;
            });
    }

    getSale() {
        return this.HttpSvc<anm.SaleResponse>('sale.json')
            .setApiUrl('https://assets.animatron.com')
            .get(this.productParams)
            .then(this.handlerHttpResponse)
            .catch((errMsg: string) => this.$q.reject(errMsg));
    }

    getCardDetails() {
        return this.HttpSvc<anm.CardDetails>('subscription', 'history')
            .secureGet(this.productParams)
            .then(this.handlerHttpResponse)
            .then((res: anm.CardDetails) => {
                this.isStripeError = res.card.type === 'N/A';
                this.checkExpired(res.card.expires);
                this.cardDetailsCashed = res;
                return res;
            })
            .catch((errMsg: string) => {
                this.isStripeError = true;
                this.cardDetailsErrorMessage = errMsg;
                return this.$q.reject(errMsg);
            });
    }

    getUpgradesByName(name: string) {
        const upgrade = this.getDetails().upgrades.filter(
            upgradeItem =>
                upgradeItem &&
                (upgradeItem.name === name || upgradeItem.displayName === name)
        )[0];
        return upgrade && this.prepareSubscription(upgrade);
    }

    getDowngradesByName(name: string, displayName?: string) {
        return this.getDowngrades().then(downgrades => {
            const downgrade = downgrades.filter(
                downgradeItem =>
                    downgradeItem &&
                    (downgradeItem.name === name ||
                        downgradeItem.displayName === displayName)
            )[0];
            return downgrade && this.prepareSubscription(downgrade);
        });
    }

    getUpgrades() {
        return this.getDetails().upgrades.filter(
            upgrade =>
                !upgrade.excludeFromUI &&
                !upgrade.hidden &&
                upgrade.name !== 'WaveEnterprise'
        );
    }

    getDowngrades(): ng.IPromise<anm.SubscriptionDetails[]> {
        return this.HttpSvc('subscription', 'downgrade')
            .secureGet(this.productParams)
            .then(this.handlerHttpResponse)
            .then((downgrades: anm.SubscriptionDetails[]) => {
                return downgrades.filter(
                    downgrade => !downgrade.excludeFromUI && !downgrade.hidden
                );
            });
    }

    cancelSubscription() {
        const { displayName, billingPeriod } = this.getDetails();

        return this.HttpSvc('subscription', 'cancel')
            .secureDelete(this.productParams)
            .then(this.handlerHttpResponse)
            .then(() => {
                this.cardDetailsCashed = null;
                this.cardDetailsErrorMessage = null;

                this.analyticSvc.changeSubscription(
                    'downgrade',
                    displayName,
                    billingPeriod
                );
                return this.AuthenticationSvc.updateUser();
            });
    }

    checkExpired(expires: string) {
        const dateArr = expires.split('/');

        if (dateArr.length === 2) {
            const monthDays = Utils.getMonthDaysCount().toString();
            dateArr.splice(1, 0, monthDays);
        }

        const formattedDate = dateArr.join('/');
        const expiredDate = new Date(formattedDate).getTime();

        const currentDate = _.now();

        this.isExpired = currentDate >= expiredDate;
    }

    changeSubscription(params: anm.ChangeSubscriptionRequest) {
        const request = () =>
            this.HttpSvc('subscription', 'change').securePut(params, {
                headers: this.analyticSvc.getGaHeader()
            });

        return this.changeSubscriptionErrorHandler(request);
    }

    private changeSubscriptionErrorHandler(request: Function) {
        const getErrorPromise = () => {
            const message = 'credit.card.processing.error.[expired_card]';
            const deferred = this.$q.defer();
            deferred.resolve({ data: { code: 400, message } });
            return deferred.promise;
        };

        return this.isExpired ? getErrorPromise() : request();
    }

    updateCard(token: string) {
        const url = `${options.apiDomain}/subscription/card/update`;
        const headers = _.extend(
            {
                'Content-Type': 'application/json'
            },
            options.headers
        );

        return this.$http
            .post(url, { token }, { headers })
            .then((r: ng.IHttpPromiseCallbackArg<any>) => {
                this.$rootScope.$emit(UPDATE_SUBSCRIPTION_CARD);
                return r;
            })
            .catch((err: ng.IHttpPromiseCallbackArg<any>) =>
                err.status !== -1
                    ? err
                    : {
                          data: {
                              code: 400,
                              message: 'subscription.update-card.default-error'
                          }
                      }
            );
    }

    round(num: number) {
        return Math.ceil(num);
    }

    calcDiscount(discount: number) {
        return discount ? 1 - discount / 100 : 1;
    }

    makeSpecialSubscription(subscription: anm.SubscriptionDetails) {
        subscription.monthlyPrice = undefined;
        return subscription;
    }

    private prepareSubscription(subscription: anm.SubscriptionDetails) {
        return this.specialSubscriptionNames.includes(subscription.name)
            ? this.makeSpecialSubscription(subscription)
            : subscription;
    }

    showSubscriptionCanceledMessage() {
        const details = this.getDetails();
        const currentPlan = details.displayName;
        const expTime = details.expirationDate;
        const date = this.$filter('date')(expTime * 1000, 'short');
        const msg = expTime
            ? `Subscription will be canceled on ${date}`
            : 'Already canceled';

        _.notification.showSuccess(msg);
    }

    private handlerHttpResponse = (
        res: ng.IHttpPromiseCallbackArg<any & ApiError>
    ) => {
        const deferred = this.$q.defer();
        if (res && res.data && Utils.isApiError(res.data)) {
            deferred.reject(res.data.message);
            console.error('subscription error: ', res.data);
        } else {
            deferred.resolve(res.data);
        }
        return deferred.promise;
    }

    private getStripeCardError(error: string) {
        const matched = error.match(/\[([A-Za-z_]+)\]/);
        const stripeKey = matched && matched[1];
        return stripeKey && this.TranslateSvc.has(stripeKey)
            ? this.TranslateSvc.get(stripeKey)
            : null;
    }

    private getStripeCouponError(error: string) {
        const errorsCode = [
            'Coupon expired',
            'No such coupon',
            'Coupon is not valid',
            'Coupon inapplicable'
        ];

        const reg = new RegExp(errorsCode.join('|'));
        const isCouponError = _.isString(error) && reg.test(error);
        return isCouponError && this.TranslateSvc.get('coupon.error');
    }

    private getRequiredSubscriptionError(error: string) {
        const isRequiredSubscriptionError =
            _.isString(error) && /required subscription:/.test(error);
        if (!isRequiredSubscriptionError) return false;

        const [, plan, period] = error.match(
            /\(([0-9a-zA-Z]+)\|([0-9a-zA-Z]+)\)/
        );
        const subscriptionName = `${plan} ${period}`;
        const productName = Utils.getProduct().toLowerCase();
        const { coupon } = this.$state.params;
        const upgradeParams = Utils.toParams({
            plan,
            coupon,
            period: period.toLowerCase()
        });

        const productUrl =
            Utils.getProduct() === 'WAVE' ? '' : `/${productName}`;
        const url = `${productUrl}/pricing/upgrade?${upgradeParams}`;

        return this.TranslateSvc.get(
            'required.subscription.error',
            url,
            subscriptionName
        );
    }
}

angular
    .module('animatronApp.SubscriptionSvc', [])
    .service('subscriptionSvc', SubscriptionSvc);



// WEBPACK FOOTER //
// ./app/assets/javascripts/settings/services/SubscriptionSvc.ts